import { useContext, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { BannerContext } from '../contexts/BannerContext';
import { AuthContext } from '../contexts/AuthContext';
import usePusherPriceAlerts from '../hooks/usePusherPriceAlerts';
import { queryKeys } from '../hooks/useTradeStationQueries';
import alertSound from '../assets/sounds/alert.wav';

/**
 * Component that listens for price alerts via Pusher and shows notifications.
 * Should be placed inside the provider hierarchy in Environment.js
 */
const PriceAlertListener = () => {
  const { showMessage } = useContext(BannerContext);
  const { user, isInitializing } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Silent initialization tracking
  useEffect(() => {
    // Component state tracking (removed noisy logs)
  }, [user, isInitializing]);

  const handleAlert = useCallback((alert) => {
    const direction = alert.alertType === 'above' || alert.alertType === 'cross_above'
      ? 'crossed above'
      : 'crossed below';

    const message = `🚨 ${alert.ticker}: Price ${direction} $${parseFloat(alert.priceLevel).toFixed(2)} (Current: $${parseFloat(alert.triggerPrice).toFixed(2)})`;

    // Persistent banner — clicking the message navigates to the trade page with symbol pre-selected.
    showMessage(message, 'warning', false, 'notification', () => {
      navigate(`/trade?symbol=${encodeURIComponent(alert.ticker)}`);
    });

    // Invalidate trade alerts query to refresh the alerts table
    queryClient.invalidateQueries({ queryKey: queryKeys.tradeAlerts });

    // Play alert sound
    try {
      const audio = new Audio(alertSound);
      audio.volume = 0.5;
      audio.play().catch((err) => {
        console.warn('[PriceAlertListener] Could not play alert sound:', err);
      });
    } catch (e) {
      console.warn('[PriceAlertListener] Error creating audio:', e);
    }

    // Fire a browser Notification so the app can be opened/focused from the background.
    // Service worker handles the click → /trade?symbol= navigation.
    if ('Notification' in window && Notification.permission === 'granted' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.showNotification(`🚨 ${alert.ticker} Alert`, {
          body: message,
          icon: '/web-app-manifest-192x192.png',
          badge: '/favicon.png',
          tag: `price-alert-${alert.ticker}-${Date.now()}`,
          renotify: true,
          data: { symbol: alert.ticker, type: 'price-alert' },
        });
      }).catch(() => {});
    }
  }, [showMessage, queryClient, navigate]);

  // Subscribe to price alerts
  const { isSubscribed, lastAlert } = usePusherPriceAlerts(handleAlert);

  // This component doesn't render anything - it just listens
  return null;
};

export default PriceAlertListener;
